import posthog from 'posthog-js';

/** Property bag for PostHog events and person properties. */
export type AnalyticsProperties = Record<string, unknown>;

/**
 * True once posthog.init() ran in main.tsx. Without a project token (local dev,
 * tests) PostHog is never loaded and every call below is a no-op.
 */
export function isAnalyticsEnabled(): boolean {
  return posthog.__loaded === true;
}

/** Runs a PostHog call so that analytics can never break the calling flow. */
function safely(call: () => void): void {
  if (!isAnalyticsEnabled()) return;

  try {
    call();
  } catch (error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] PostHog call failed', error);
    }
  }
}

/**
 * Binds the PostHog person to our user ID.
 * `propertiesToSetOnce` uses `$set_once` — it never overwrites an existing value.
 */
export function identifyUser(
  distinctId: string,
  properties: AnalyticsProperties,
  propertiesToSetOnce?: AnalyticsProperties
): void {
  safely(() => posthog.identify(distinctId, properties, propertiesToSetOnce));
}

/** Updates person properties of the currently identified person. */
export function setPersonProperties(
  properties: AnalyticsProperties,
  propertiesToSetOnce?: AnalyticsProperties
): void {
  safely(() => posthog.setPersonProperties(properties, propertiesToSetOnce));
}

/** Sends a custom event for the currently identified person. */
export function captureEvent(event: string, properties?: AnalyticsProperties): void {
  safely(() => posthog.capture(event, properties));
}

/** Drops the identified person and starts a fresh anonymous session. */
export function resetAnalytics(): void {
  safely(() => posthog.reset());
}
